import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Menu,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Save as SaveIcon,
} from '@mui/icons-material';
import { styled, useTheme } from '@mui/material/styles';
import { BglPrimaryButton } from 'components/button/BglButtons';
import { useGlobalConfig } from 'context/GlobalContext';
import { ROUTE_TREE } from 'routes/AppRoutes';
import { ArrowLeft, Key, Plus, Check, X, EyeOff, Info, ChevronDown } from 'lucide-react';

// Styled components
const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: 12,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  transition: 'all 0.2s ease-in-out',
}));

const AccessButton = styled(Button)<{ status: 'access' | 'pass' | 'kill' }>(({ theme, status }) => ({
  width: 100,
  textTransform: 'none',
  fontWeight: 500,
  borderRadius: 6,
  padding: '4px 8px',
  gap: 4,
  minHeight: '30px',
  fontSize: '12px',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  boxShadow: 'none',
  ...(status === 'access' && {
    backgroundColor: '#e8f5e8', // Light green background
    color: '#2e7d32', // Dark green text
    border: '1px solid #c8e6c9',
    '&:hover': {
      backgroundColor: '#d4edda',
      border: '1px solid #2e7d32',
    },
  }),
  ...(status === 'pass' && {
    backgroundColor: '#fff3e0', // Light orange background
    color: '#f57c00', // Dark orange text
    border: '1px solid #ffcc02',
    '&:hover': {
      backgroundColor: '#ffe0b2',
      border: '1px solid #f57c00',
    },
  }),
  ...(status === 'kill' && {
    backgroundColor: '#ffebee', // Light red background
    color: '#d32f2f', // Dark red text
    border: '1px solid #ffcdd2',
    '&:hover': {
      backgroundColor: '#ffcdd2',
      border: '1px solid #d32f2f',
    },
  }),
}));

// Mock data for features
const mockFeatures = [
  {
    id: 'EMAIL.SF.MONTHLY_SUMMARY',
    name: 'EMAIL.SF.MONTHLY_SUMMARY',
    default: 'kill' as const,
    eeTreatment: 'pass' as const,
    eeControl: 'pass' as const,
  },
  {
    id: 'EMAIL.DF.MONTHLY_SUMMARY.ELECTRIC',
    name: 'EMAIL.DF.MONTHLY_SUMMARY.ELECTRIC',
    default: 'access' as const,
    eeTreatment: 'access' as const,
    eeControl: 'pass' as const,
  },
  {
    id: 'EMAIL.DF.MONTHLY_SUMMARY.GAS',
    name: 'EMAIL.DF.MONTHLY_SUMMARY.GAS',
    default: 'access' as const,
    eeTreatment: 'pass' as const,
    eeControl: 'pass' as const,
  },
  {
    id: 'EMAIL.DF.MONTHLY_SUMMARY.WATER',
    name: 'EMAIL.DF.MONTHLY_SUMMARY.WATER',
    default: 'access' as const,
    eeTreatment: 'pass' as const,
    eeControl: 'pass' as const,
  },
  {
    id: 'EMAIL.SF.BILL_PROJECTION',
    name: 'EMAIL.SF.BILL_PROJECTION',
    default: 'access' as const,
    eeTreatment: 'pass' as const,
    eeControl: 'pass' as const,
  },
  {
    id: 'EMAIL.DF.BILL_PROJECTION.ELECTRIC',
    name: 'EMAIL.DF.BILL_PROJECTION.ELECTRIC',
    default: 'access' as const,
    eeTreatment: 'pass' as const,
    eeControl: 'pass' as const,
  },
  {
    id: 'EMAIL.DF.BILL_PROJECTION.GAS',
    name: 'EMAIL.DF.BILL_PROJECTION.GAS',
    default: 'access' as const,
    eeTreatment: 'pass' as const,
    eeControl: 'pass' as const,
  },
  {
    id: 'EMAIL.DF.BILL_PROJECTION.WATER',
    name: 'EMAIL.DF.BILL_PROJECTION.WATER',
    default: 'access' as const,
    eeTreatment: 'pass' as const,
    eeControl: 'pass' as const,
  },
  {
    id: 'EMAIL.SF.BUDGET_ALERT',
    name: 'EMAIL.SF.BUDGET_ALERT',
    default: 'access' as const,
    eeTreatment: 'pass' as const,
    eeControl: 'pass' as const,
  },
  {
    id: 'EMAIL.DF.BUDGET_ALERT.ELECTRIC',
    name: 'EMAIL.DF.BUDGET_ALERT.ELECTRIC',
    default: 'access' as const,
    eeTreatment: 'pass' as const,
    eeControl: 'pass' as const,
  },
  {
    id: 'EMAIL.DF.BUDGET_ALERT.GAS',
    name: 'EMAIL.DF.BUDGET_ALERT.GAS',
    default: 'access' as const,
    eeTreatment: 'pass' as const,
    eeControl: 'pass' as const,
  },
  {
    id: 'EMAIL.DF.BUDGET_ALERT.WATER',
    name: 'EMAIL.DF.BUDGET_ALERT.WATER',
    default: 'access' as const,
    eeTreatment: 'pass' as const,
    eeControl: 'pass' as const,
  },
  {
    id: 'EMAIL.SF.NEIGHBOURHOOD_COMPARISON',
    name: 'EMAIL.SF.NEIGHBOURHOOD_COMPARISON',
    default: 'access' as const,
    eeTreatment: 'pass' as const,
    eeControl: 'pass' as const,
  },
  {
    id: 'EMAIL.CDF.NEIGHBOURHOOD_COMPARISON',
    name: 'EMAIL.CDF.NEIGHBOURHOOD_COMPARISON',
    default: 'access' as const,
    eeTreatment: 'pass' as const,
    eeControl: 'pass' as const,
  },
];

export const FeatureAccessPage: React.FC = () => {
  const { rc } = useGlobalConfig();
  const theme = useTheme();
  
  // State
  const [features, setFeatures] = useState(mockFeatures);
  const [filter, setFilter] = useState('all');
  const [hasChanges, setHasChanges] = useState(false);
  const [dropdownAnchor, setDropdownAnchor] = useState<{ [key: string]: HTMLElement | null }>({});
  const [addClusterAnchor, setAddClusterAnchor] = useState<null | HTMLElement>(null);
  const addClusterOpen = Boolean(addClusterAnchor);

  const handleBack = () => {
    rc.uiRouter.navigate(ROUTE_TREE.CONFIG_TOOLS.DCX_CLUSTERS);
  };

  const getNextStatus = (currentStatus: 'access' | 'pass' | 'kill'): 'access' | 'pass' | 'kill' => {
    switch (currentStatus) {
      case 'access':
        return 'pass';
      case 'pass':
        return 'kill';
      case 'kill':
        return 'access';
      default:
        return 'access';
    }
  };

  const handleAccessChange = (featureId: string, column: 'default' | 'eeTreatment' | 'eeControl', newStatus: 'access' | 'pass' | 'kill') => {
    setFeatures(prev => prev.map(feature => 
      feature.id === featureId 
        ? { ...feature, [column]: newStatus }
        : feature
    ));
    setHasChanges(true);
  };

  const handleDropdownOpen = (event: React.MouseEvent<HTMLElement>, featureId: string, column: string) => {
    const key = `${featureId}-${column}`;
    setDropdownAnchor(prev => ({ ...prev, [key]: event.currentTarget }));
  };

  const handleDropdownClose = (featureId: string, column: string) => {
    const key = `${featureId}-${column}`;
    setDropdownAnchor(prev => ({ ...prev, [key]: null }));
  };

  const handleSaveChanges = () => {
    // Simulate API call
    console.log('Saving feature access changes:', features);
    setHasChanges(false);
  };

  const handleAddClusterClick = (event: React.MouseEvent<HTMLElement>) => {
    setAddClusterAnchor(event.currentTarget);
  };

  const handleAddClusterClose = () => {
    setAddClusterAnchor(null);
  };

  const handleClusterTypeSelect = (clusterType: string) => {
    console.log('Selected cluster type:', clusterType);
    handleAddClusterClose();
  };

  const dropdownOptions = [
    { label: 'EV USERS', value: 'ev_users' },
    { label: 'AMI', value: 'ami' },
    { label: 'AMR', value: 'amr' },
    { label: 'NSM', value: 'nsm' },
  ];

  const getStatusIcon = (status: 'access' | 'pass' | 'kill') => {
    switch (status) {
      case 'access':
        return <Check size={12} style={{ color: '#2e7d32' }} />;
      case 'pass':
        return <EyeOff size={12} style={{ color: '#f57c00' }} />;
      case 'kill':
        return <X size={12} style={{ color: '#d32f2f' }} />;
    }
  };

  const filteredFeatures = filter === 'all' 
    ? features 
    : features.filter(feature => feature.name.toLowerCase().includes(filter.toLowerCase()));

  return (
    <Box sx={{ 
      minHeight: '100vh',
      bgcolor: 'background.default',
      p: 3,
    }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            Feature Access
          </Typography>
          <Button
            variant="outlined"
            startIcon={<ArrowLeft size={16} />}
            onClick={handleBack}
            sx={{ color: 'text.secondary', borderColor: 'divider' }}
          >
            Back to Cluster Configuration
          </Button>
        </Box>
      </Box>

      {/* Main Content */}
      <Box sx={{ mx: 'auto' }}>
        <StyledCard>
          <CardContent sx={{ p: 4,textAlign:'left' }}>
            {/* Feature Access Matrix Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: .5 }}>
                    <Key size={24} style={{ marginRight: 12, color: theme.palette.primary.main }} />
                    <Typography variant="h5" sx={{ fontWeight: 600}}>
                        Feature Access Matrix
                    </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Manage feature accessibility based on 'Feature' category clusters. This list is derived from the Application Scope page; only enabled applications are displayed here.
                </Typography>
              </Box>
              <Box sx={{ ml: 'auto' }}>
                <Button
                  variant="outlined"
                  startIcon={<Plus size={16} />}
                  endIcon={<ChevronDown size={16} />}
                  onClick={handleAddClusterClick}
                  aria-controls={addClusterOpen ? 'add-cluster-menu' : undefined}
                  aria-haspopup="true"
                  aria-expanded={addClusterOpen ? 'true' : undefined}
                  sx={{ color: 'text.secondary', borderColor: 'divider' }}
                >
                  Add Cluster
                </Button>
                <Menu
                  id="add-cluster-menu"
                  anchorEl={addClusterAnchor}
                  open={addClusterOpen}
                  onClose={handleAddClusterClose}
                  MenuListProps={{
                    'aria-labelledby': 'add-cluster-button',
                  }}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                  PaperProps={{
                    sx: {
                      borderRadius: 2,
                      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
                      border: '1px solid #E0E0E0',
                      minWidth: '160px',
                      '& .MuiMenuItem-root': {
                        padding: '12px 16px',
                        fontSize: '14px',
                        fontWeight: 500,
                        '&:hover': {
                          backgroundColor: '#f5f5f5',
                        },
                      },
                    },
                  }}
                >
                  {dropdownOptions.map((option) => (
                    <MenuItem 
                      key={option.value} 
                      onClick={() => handleClusterTypeSelect(option.value)}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </Menu>
              </Box>
            </Box>

            {/* Filter */}
            <Box sx={{ mb: 3 }}>
              <FormControl size="small" sx={{ minWidth: 300 }}>
                <Typography variant="body2" sx={{ color: 'text.secondary',marginBottom:.5 }}>Filter by Feature</Typography>
                <Select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                //   label=""
                >
                  <MenuItem value="all">All Features</MenuItem>
                  {features.map((feature) => (
                    <MenuItem key={feature.id} value={feature.id}>{feature.name}</MenuItem>
                  ))}
                  {/* <MenuItem value="email">Email Features</MenuItem>
                  <MenuItem value="dashboard">Dashboard Features</MenuItem> */}
                </Select>
              </FormControl>
            </Box>

            {/* Feature Access Table */}
            <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'background.default' }}>
                    <TableCell sx={{ fontWeight: 600 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        Feature
                        <Tooltip title="Feature access configuration">
                          <Info size={16} style={{ marginLeft: 4, color: theme.palette.text.secondary }} />
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>DEFAULT</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>EE TREATMENT</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>EE CONTROL (Old)</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredFeatures.map((feature) => (
                    <TableRow key={feature.id} hover>
                      <TableCell sx={{px:1,py:0.5}}>
                        <Typography sx={{ fontFamily: 'monospace', fontSize:'12px' }}>
                          {feature.name}
                        </Typography>
                      </TableCell>
                      <TableCell align="center" sx={{px:1,py:0.5, textAlign: 'center'}}>
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                          <AccessButton
                            status={feature.default}
                            onClick={(e) => handleDropdownOpen(e, feature.id, 'default')}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: .5 }}>
                              {getStatusIcon(feature.default)}
                              {feature.default.toUpperCase()}
                            </Box>
                            <ChevronDown size={14} />
                          </AccessButton>
                        </Box>
                        <Menu
                          anchorEl={dropdownAnchor[`${feature.id}-default`]}
                          open={Boolean(dropdownAnchor[`${feature.id}-default`])}
                          onClose={() => handleDropdownClose(feature.id, 'default')}
                          PaperProps={{
                            sx: {
                              borderRadius: 2,
                              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                              minWidth: 120,
                              '& .MuiMenuItem-root': {
                                padding: '8px 16px',
                                fontSize: '12px',
                                fontWeight: 500,
                                '&:hover': {
                                  backgroundColor: '#f5f5f5',
                                },
                                '&.selected': {
                                  backgroundColor: '#2e7d32',
                                  color: 'white',
                                  '& .MuiListItemIcon-root': {
                                    color: 'white',
                                  },
                                },
                              },
                            },
                          }}
                        >
                          <MenuItem 
                            onClick={() => { handleAccessChange(feature.id, 'default', 'access'); handleDropdownClose(feature.id, 'default'); }}
                            className={String(feature.default) === 'access' ? 'selected' : ''}
                          >
                            <ListItemIcon sx={{ minWidth: 24 }}>
                              <Check size={16} style={{ color: String(feature.default) === 'access' ? 'white' : '#2e7d32' }} />
                            </ListItemIcon>
                            <ListItemText>ACCESS</ListItemText>
                          </MenuItem>
                          <MenuItem 
                            onClick={() => { handleAccessChange(feature.id, 'default', 'pass'); handleDropdownClose(feature.id, 'default'); }}
                            className={String(feature.default) === 'pass' ? 'selected' : ''}
                          >
                            <ListItemIcon sx={{ minWidth: 24 }}>
                              <EyeOff size={16} style={{ color: String(feature.default) === 'pass' ? 'white' : '#f57c00' }} />
                            </ListItemIcon>
                            <ListItemText>PASS</ListItemText>
                          </MenuItem>
                          <MenuItem 
                            onClick={() => { handleAccessChange(feature.id, 'default', 'kill'); handleDropdownClose(feature.id, 'default'); }}
                            className={String(feature.default) === 'kill' ? 'selected' : ''}
                          >
                            <ListItemIcon sx={{ minWidth: 24 }}>
                              <X size={16} style={{ color: String(feature.default) === 'kill' ? 'white' : '#d32f2f' }} />
                            </ListItemIcon>
                            <ListItemText>KILL</ListItemText>
                          </MenuItem>
                        </Menu>
                      </TableCell>
                      <TableCell align="center" sx={{px:1,py:0.5, textAlign: 'center'}}>
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                          <AccessButton
                            status={feature.eeTreatment}
                            onClick={(e) => handleDropdownOpen(e, feature.id, 'eeTreatment')}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {getStatusIcon(feature.eeTreatment)}
                              {feature.eeTreatment.toUpperCase()}
                            </Box>
                            <ChevronDown size={14} />
                          </AccessButton>
                        </Box>
                        <Menu
                          anchorEl={dropdownAnchor[`${feature.id}-eeTreatment`]}
                          open={Boolean(dropdownAnchor[`${feature.id}-eeTreatment`])}
                          onClose={() => handleDropdownClose(feature.id, 'eeTreatment')}
                          PaperProps={{
                            sx: {
                              borderRadius: 2,
                              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                              minWidth: 120,
                              '& .MuiMenuItem-root': {
                                padding: '8px 16px',
                                fontSize: '12px',
                                fontWeight: 500,
                                '&:hover': {
                                  backgroundColor: '#f5f5f5',
                                },
                                '&.selected': {
                                  backgroundColor: '#2e7d32',
                                  color: 'white',
                                  '& .MuiListItemIcon-root': {
                                    color: 'white',
                                  },
                                },
                              },
                            },
                          }}
                        >
                          <MenuItem 
                            onClick={() => { handleAccessChange(feature.id, 'eeTreatment', 'access'); handleDropdownClose(feature.id, 'eeTreatment'); }}
                            className={String(feature.eeTreatment) === 'access' ? 'selected' : ''}
                          >
                            <ListItemIcon sx={{ minWidth: 24 }}>
                              <Check size={16} style={{ color: String(feature.eeTreatment) === 'access' ? 'white' : '#2e7d32' }} />
                            </ListItemIcon>
                            <ListItemText>ACCESS</ListItemText>
                          </MenuItem>
                          <MenuItem 
                            onClick={() => { handleAccessChange(feature.id, 'eeTreatment', 'pass'); handleDropdownClose(feature.id, 'eeTreatment'); }}
                            className={String(feature.eeTreatment) === 'pass' ? 'selected' : ''}
                          >
                            <ListItemIcon sx={{ minWidth: 24 }}>
                              <EyeOff size={16} style={{ color: String(feature.eeTreatment) === 'pass' ? 'white' : '#f57c00' }} />
                            </ListItemIcon>
                            <ListItemText>PASS</ListItemText>
                          </MenuItem>
                          <MenuItem 
                            onClick={() => { handleAccessChange(feature.id, 'eeTreatment', 'kill'); handleDropdownClose(feature.id, 'eeTreatment'); }}
                            className={String(feature.eeTreatment) === 'kill' ? 'selected' : ''}
                          >
                            <ListItemIcon sx={{ minWidth: 24 }}>
                              <X size={16} style={{ color: String(feature.eeTreatment) === 'kill' ? 'white' : '#d32f2f' }} />
                            </ListItemIcon>
                            <ListItemText>KILL</ListItemText>
                          </MenuItem>
                        </Menu>
                      </TableCell>
                      <TableCell align="center" sx={{px:1,py:0.5, textAlign: 'center'}}>
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                          <AccessButton
                            status={feature.eeControl}
                            onClick={(e) => handleDropdownOpen(e, feature.id, 'eeControl')}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {getStatusIcon(feature.eeControl)}
                              {feature.eeControl.toUpperCase()}
                            </Box>
                            <ChevronDown size={14} />
                          </AccessButton>
                        </Box>
                        <Menu
                          anchorEl={dropdownAnchor[`${feature.id}-eeControl`]}
                          open={Boolean(dropdownAnchor[`${feature.id}-eeControl`])}
                          onClose={() => handleDropdownClose(feature.id, 'eeControl')}
                          PaperProps={{
                            sx: {
                              borderRadius: 2,
                              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                              minWidth: 120,
                              '& .MuiMenuItem-root': {
                                padding: '8px 16px',
                                fontSize: '12px',
                                fontWeight: 500,
                                '&:hover': {
                                  backgroundColor: '#f5f5f5',
                                },
                                '&.selected': {
                                  backgroundColor: '#2e7d32',
                                  color: 'white',
                                  '& .MuiListItemIcon-root': {
                                    color: 'white',
                                  },
                                },
                              },
                            },
                          }}
                        >
                          <MenuItem 
                            onClick={() => { handleAccessChange(feature.id, 'eeControl', 'access'); handleDropdownClose(feature.id, 'eeControl'); }}
                            className={String(feature.eeControl) === 'access' ? 'selected' : ''}
                          >
                            <ListItemIcon sx={{ minWidth: 24 }}>
                              <Check size={16} style={{ color: String(feature.eeControl) === 'access' ? 'white' : '#2e7d32' }} />
                            </ListItemIcon>
                            <ListItemText>ACCESS</ListItemText>
                          </MenuItem>
                          <MenuItem 
                            onClick={() => { handleAccessChange(feature.id, 'eeControl', 'pass'); handleDropdownClose(feature.id, 'eeControl'); }}
                            className={String(feature.eeControl) === 'pass' ? 'selected' : ''}
                          >
                            <ListItemIcon sx={{ minWidth: 24 }}>
                              <EyeOff size={16} style={{ color: String(feature.eeControl) === 'pass' ? 'white' : '#f57c00' }} />
                            </ListItemIcon>
                            <ListItemText>PASS</ListItemText>
                          </MenuItem>
                          <MenuItem 
                            onClick={() => { handleAccessChange(feature.id, 'eeControl', 'kill'); handleDropdownClose(feature.id, 'eeControl'); }}
                            className={String(feature.eeControl) === 'kill' ? 'selected' : ''}
                          >
                            <ListItemIcon sx={{ minWidth: 24 }}>
                              <X size={16} style={{ color: String(feature.eeControl) === 'kill' ? 'white' : '#d32f2f' }} />
                            </ListItemIcon>
                            <ListItemText>KILL</ListItemText>
                          </MenuItem>
                        </Menu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Save Changes Button */}
            {hasChanges && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                <BglPrimaryButton
                  startIcon={<SaveIcon />}
                  onClick={handleSaveChanges}
                >
                  Save Changes
                </BglPrimaryButton>
              </Box>
            )}
          </CardContent>
        </StyledCard>
      </Box>
    </Box>
  );
};
